/**
 * AuthorProfileSidebar — rich "About the author" card that sits alongside
 * the topic body in the community. Shows photo/initials, name, role badge,
 * business context, machines owned, experience, tier badges, community
 * footprint (topics + replies + certs) — so readers instantly know who
 * they're talking to.
 *
 * Fed by GET /api/community/users/{id}/profile.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import {
  MapPin, Briefcase, Award, Cog, MessageSquare, GraduationCap,
  Sparkles, TrendingUp, Calendar, Star, Send,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const EXPERIENCE_META = {
  beginner:     { label: 'Beginner',     color: 'text-emerald-700 bg-emerald-500/15' },
  intermediate: { label: 'Intermediate', color: 'text-primary bg-primary/15' },
  advanced:     { label: 'Advanced',     color: 'text-amber-700 bg-amber-500/15' },
  expert:       { label: 'Expert',       color: 'text-violet-700 bg-violet-500/15' },
};

const AuthorProfileSidebar = ({ userId, catColor = '#0EA5E9' }) => {
  const { API, user: currentUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();

  const startDM = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const r = await axios.post(`${API}/dm/conversations`, { peer_id: userId });
      navigate(`/messages/${r.data.conversation.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open conversation');
    } finally { setStarting(false); }
  };

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/community/users/${userId}/profile`);
        setProfile(r.data);
      } catch { setProfile(null); }
      finally { setLoading(false); }
    })();
  }, [API, userId]);

  if (loading) {
    return (
      <aside className="pro-card p-4" data-testid="author-profile-sidebar-loading">
        <div className="h-16 w-16 rounded-full bg-muted animate-pulse mb-3" />
        <div className="h-3 w-24 bg-muted animate-pulse rounded" />
        <div className="mt-2 h-3 w-full bg-muted animate-pulse rounded" />
      </aside>
    );
  }
  if (!profile) return null;

  const initials = (profile.name || '?').slice(0, 2).toUpperCase();
  const expMeta = EXPERIENCE_META[(profile.experience_level || '').toLowerCase()] || null;
  const joined = profile.joined_at ? formatDistanceToNow(new Date(profile.joined_at), { addSuffix: false }) : null;

  return (
    <aside className="pro-card p-4 space-y-4" data-testid="author-profile-sidebar">
      {/* Header — avatar + name + role */}
      <div className="flex flex-col items-center text-center">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={profile.name}
            className="h-20 w-20 rounded-full object-cover border-2"
            style={{ borderColor: catColor }}
            data-testid="sidebar-avatar-img"
          />
        ) : (
          <div
            className="h-20 w-20 rounded-full flex items-center justify-center text-2xl font-extrabold"
            style={{ backgroundColor: `${catColor}25`, color: catColor, border: `2px solid ${catColor}` }}
            aria-hidden="true"
            data-testid="sidebar-avatar-initials"
          >
            {initials}
          </div>
        )}
        <div className="mt-2 font-heading text-base font-extrabold text-foreground">{profile.name}</div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap justify-center">
          {profile.role === 'admin' && (
            <span className="text-[9px] uppercase tracking-widest font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded">
              Admin
            </span>
          )}
          {profile.is_pro && (
            <span className="text-[9px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" /> Pro
            </span>
          )}
          {expMeta && (
            <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${expMeta.color}`}>
              {expMeta.label}
            </span>
          )}
        </div>
        {/* Message button — always visible except when we've confirmed
            you're looking at your own profile. If auth hasn't hydrated yet
            we still show it, since the click gracefully fails if unauthed. */}
        {(!currentUser || currentUser.id !== userId) && (
          <Button
            size="sm"
            onClick={startDM}
            disabled={starting}
            className="mt-3 rounded-full font-bold w-full"
            data-testid="sidebar-message-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> {starting ? 'Opening…' : 'Message'}
          </Button>
        )}
        {currentUser && currentUser.id === userId && (
          <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            This is you
          </div>
        )}
      </div>

      {/* Business + location */}
      {(profile.business_name || profile.business_type || profile.location) && (
        <div className="space-y-1.5 text-xs">
          {profile.business_name && (
            <div className="flex items-start gap-2">
              <Briefcase className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold text-foreground">{profile.business_name}</div>
                {profile.business_type && (
                  <div className="text-[10px] text-muted-foreground">{profile.business_type}</div>
                )}
              </div>
            </div>
          )}
          {!profile.business_name && profile.business_type && (
            <div className="flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground/85">{profile.business_type}</span>
            </div>
          )}
          {profile.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground/85">{profile.location}</span>
            </div>
          )}
        </div>
      )}

      {/* Experience */}
      {(profile.years_experience != null || (profile.primary_interest || []).length > 0) && (
        <div className="space-y-1.5 text-xs pt-2 border-t border-border">
          {profile.years_experience != null && (
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span>
                <strong className="text-foreground">{profile.years_experience}+</strong>
                <span className="text-muted-foreground"> {profile.years_experience === 1 ? 'year' : 'years'} freeze-drying</span>
              </span>
            </div>
          )}
          {(profile.primary_interest || []).slice(0, 4).map((interest) => (
            <div key={interest} className="flex items-center gap-2">
              <Star className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground/80 truncate">{interest}</span>
            </div>
          ))}
        </div>
      )}

      {/* Machines owned */}
      {profile.machines?.length > 0 && (
        <div className="pt-2 border-t border-border" data-testid="sidebar-machines">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5 flex items-center gap-1">
            <Cog className="w-3 h-3" /> Equipment ({profile.machine_count})
          </div>
          <div className="space-y-1">
            {profile.machines.slice(0, 3).map((m, i) => (
              m.brand && (
                <div key={i} className="text-xs">
                  <div className="font-semibold text-foreground truncate">
                    {m.brand}{m.model_size && m.model_size !== 'Unknown' ? ` · ${m.model_size}` : ''}
                  </div>
                  {m.primary_use && (
                    <div className="text-[10px] text-muted-foreground truncate">{m.primary_use}</div>
                  )}
                </div>
              )
            ))}
            {profile.machines.length > 3 && (
              <div className="text-[10px] text-muted-foreground">+{profile.machines.length - 3} more</div>
            )}
          </div>
        </div>
      )}

      {/* Community footprint */}
      <div className="pt-2 border-t border-border grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-sm font-extrabold text-foreground">{profile.topic_count}</div>
          <div className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">Topics</div>
        </div>
        <div>
          <div className="text-sm font-extrabold text-foreground">{profile.reply_count}</div>
          <div className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">Replies</div>
        </div>
        <div>
          <div className="text-sm font-extrabold text-foreground">
            {(profile.certificate_count || 0) + (profile.designation_count || 0)}
          </div>
          <div className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">Certs</div>
        </div>
      </div>

      {/* Footer */}
      {joined && (
        <div className="pt-1 text-center text-[10px] text-muted-foreground">
          Member for {joined}
        </div>
      )}
    </aside>
  );
};

export default AuthorProfileSidebar;
